import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  startDownloadSpecialistGuide,
  startLoadingSpecialistGuideOptions,
  startLoadingSpecialistGuides
} from '../../app/actions/specialistGuideActions';
import {
  countVisibleGuides,
  formatGuideBytes,
  getGuideRoleId,
  groupGuidesByRoleAndPhase,
  guideMatchesModules,
  guideMatchesPhase,
  guideMatchesRole,
  listUserGuideRoles,
  toggleNumericId
} from '../../app/helpers/specialistGuideFilterHelper';
import { getGuideAccentTone } from '../../app/helpers/specialistGuideColorHelper';
import useActiveSpecialistRole from '../../app/hooks/useActiveSpecialistRole';
import NavbarIconsGenericComponent from '../generic-nav-bar/NavbarIconsGenericComponent';
import { GuidePhaseBadge, GuideRoleBadges, SpecialistGuideMetaBadges } from './SpecialistGuideColorBadges';
import SpecialistGuideListFilters from './SpecialistGuideListFilters';
import './specialistGuides.css';

const defaultTrayState = (moduleIds) => ({
  allModules: true,
  moduleIds: [...moduleIds]
});

const SpecialistGuidesScreen = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { guides, options, loading } = useSelector((state) => state.specialistGuide);
  const configuration = useSelector((state) => state.configuration);
  const userRole = useSelector((state) => state.userRole);
  const { selectedRole, isRoleSelected } = useActiveSpecialistRole();
  const titleEnabled = options.title_enabled === true
    || String(configuration?.specialistGuidesTitleEnabled || '').toLowerCase() === 'true';
  const [filterRoleId, setFilterRoleId] = useState('');
  const [filterPhase, setFilterPhase] = useState('');
  const [filterModuleIds, setFilterModuleIds] = useState([]);
  const [trayModuleState, setTrayModuleState] = useState({});
  const lastSyncedRoleId = useRef(null);

  useEffect(() => {
    dispatch(startLoadingSpecialistGuideOptions());
    dispatch(startLoadingSpecialistGuides());
  }, [dispatch]);

  const catalogModules = useMemo(() => options.modules || [], [options.modules]);
  const allModuleIds = useMemo(
    () => catalogModules.map((item) => Number(item.id)),
    [catalogModules]
  );
  const userGuideRoles = useMemo(
    () => listUserGuideRoles(options.roles || [], userRole),
    [options.roles, userRole]
  );
  const topFilterActive = Boolean(filterRoleId) || Boolean(filterPhase) || filterModuleIds.length > 0;
  const showTrayModuleFilters = !topFilterActive;

  useEffect(() => {
    if (!isRoleSelected || !selectedRole?.id) {
      return;
    }
    const match = userGuideRoles.find((role) => Number(role.id) === Number(selectedRole.id));
    if (!match) {
      return;
    }
    const nextId = Number(match.id);
    if (lastSyncedRoleId.current === nextId) {
      return;
    }
    lastSyncedRoleId.current = nextId;
    setFilterRoleId(String(nextId));
  }, [isRoleSelected, selectedRole?.id, userGuideRoles]);

  const filteredGuides = useMemo(
    () => (guides || []).filter((row) => (
      guideMatchesRole(row, filterRoleId)
      && guideMatchesPhase(row, filterPhase)
      && guideMatchesModules(row, filterModuleIds)
    )),
    [guides, filterRoleId, filterPhase, filterModuleIds]
  );

  const trays = useMemo(() => {
    const grouped = groupGuidesByRoleAndPhase(filteredGuides, options.roles || []);
    if (!filterRoleId) {
      return grouped;
    }
    const targetRoleId = Number(filterRoleId);
    return grouped.filter((tray) => getGuideRoleId(tray.role) === targetRoleId);
  }, [filteredGuides, options.roles, filterRoleId]);

  const getTrayFilter = (roleId) => trayModuleState[roleId] || defaultTrayState(allModuleIds);

  const setTrayFilter = (roleId, next) => {
    setTrayModuleState((prev) => ({ ...prev, [roleId]: next }));
  };

  const guidesForTray = (roleId, roleGuides) => {
    if (topFilterActive) {
      return roleGuides;
    }
    const trayFilter = getTrayFilter(roleId);
    if (trayFilter.allModules) {
      return roleGuides;
    }
    return roleGuides.filter((row) => guideMatchesModules(row, trayFilter.moduleIds));
  };

  const onClearFilters = () => {
    setFilterRoleId('');
    setFilterPhase('');
    setFilterModuleIds([]);
    setTrayModuleState({});
  };

  const renderCard = (row, groupKey) => {
    const accent = getGuideAccentTone(row);
    return (
      <div className="col-md-6 col-xl-4" key={`${groupKey}-${row.id}`}>
        <div className="card h-100 bg-light border-0 sg-card" style={{ '--sg-accent': accent.accent }}>
          <div className="card-body">
            <div className="mb-2">
              <SpecialistGuideMetaBadges
                roles={row.roles}
                phase={row.guide_phase}
                modules={row.modules}
                appliesToAll={row.applies_to_all_modules}
                t={t}
              />
            </div>
            {titleEnabled && row.title ? <h5 className="card-title">{row.title}</h5> : null}
            <p className="mb-2 fw-semibold" style={{ whiteSpace: 'pre-wrap' }}>{row.description}</p>
            <div className="small text-muted">{row.original_filename} · {formatGuideBytes(row.size_bytes)}</div>
          </div>
          <div className="card-footer bg-light border-0">
            <button
              type="button"
              className="btn btn-sm btn-dark"
              onClick={() => dispatch(startDownloadSpecialistGuide(row.id, row.original_filename))}
            >
              {t('specialistGuides.download')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const trayKey = (tray) => `${getGuideRoleId(tray.role)}::${tray.phase || ''}`;

  const visibleGuides = trays.flatMap((tray) => guidesForTray(trayKey(tray), tray.guides));
  const visibleDocumentCount = countVisibleGuides(visibleGuides);
  const visibleTrayCount = trays.reduce((count, tray) => {
    return count + (guidesForTray(trayKey(tray), tray.guides).length ? 1 : 0);
  }, 0);

  return (
    <>
      <NavbarIconsGenericComponent componentName="SpecialistGuides" />
      <div className="container mt-4 mb-5">
        <h3>{t('specialistGuides.title')}</h3>
        <p className="text-muted">{t('specialistGuides.subtitle')}</p>

        <SpecialistGuideListFilters
          t={t}
          roles={userGuideRoles}
          modules={catalogModules}
          filterRoleId={filterRoleId}
          filterPhase={filterPhase}
          filterModuleIds={filterModuleIds}
          showRoleFilter={userGuideRoles.length > 1}
          visibleCount={visibleDocumentCount}
          onRoleChange={setFilterRoleId}
          onPhaseChange={setFilterPhase}
          onModulesChange={setFilterModuleIds}
          onClear={onClearFilters}
        />

        {loading ? <p>{t('specialistGuides.loading')}</p> : null}

        {trays.map((tray) => {
          const sectionKey = trayKey(tray);
          const trayFilter = getTrayFilter(sectionKey);
          const visibleGuides = guidesForTray(sectionKey, tray.guides);
          if (!visibleGuides.length) {
            return null;
          }
          return (
            <section className="mb-4" key={sectionKey}>
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                <h5 className="mb-0 d-flex flex-wrap align-items-center gap-2">
                  <GuideRoleBadges roles={[tray.role]} t={t} />
                  <GuidePhaseBadge phase={tray.phase} />
                </h5>
                {showTrayModuleFilters ? (
                  <div className="d-flex flex-wrap align-items-center gap-3">
                    <div className="form-check mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`tray-all-modules-${sectionKey}`}
                        checked={!!trayFilter.allModules}
                        onChange={(event) => {
                          const checked = event.target.checked;
                          setTrayFilter(sectionKey, {
                            allModules: checked,
                            moduleIds: checked ? allModuleIds : [...allModuleIds]
                          });
                        }}
                      />
                      <label className="form-check-label" htmlFor={`tray-all-modules-${sectionKey}`}>
                        {t('specialistGuides.fields.allModules')}
                      </label>
                    </div>
                    {!trayFilter.allModules ? (
                      catalogModules.map((moduleItem) => {
                        const moduleId = Number(moduleItem.id);
                        return (
                          <div className="form-check mb-0" key={`${sectionKey}-${moduleId}`}>
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`tray-mod-${sectionKey}-${moduleId}`}
                              checked={trayFilter.moduleIds.includes(moduleId)}
                              onChange={() => setTrayFilter(sectionKey, {
                                ...trayFilter,
                                moduleIds: toggleNumericId(trayFilter.moduleIds, moduleId)
                              })}
                            />
                            <label className="form-check-label" htmlFor={`tray-mod-${sectionKey}-${moduleId}`}>
                              {moduleItem.name}
                            </label>
                          </div>
                        );
                      })
                    ) : null}
                  </div>
                ) : null}
              </div>
              <div className="row g-3">{visibleGuides.map((row) => renderCard(row, sectionKey))}</div>
            </section>
          );
        })}

        {!loading && (guides || []).length === 0 ? (
          <p className="text-muted">{t('specialistGuides.empty')}</p>
        ) : null}

        {!loading && (guides || []).length > 0 && visibleTrayCount === 0 ? (
          <p className="text-muted">{t('specialistGuides.filters.empty')}</p>
        ) : null}
      </div>
    </>
  );
};

export default SpecialistGuidesScreen;
